/**
 * Privacy Settings tab content.
 *
 * Displays the user's current consent choices and allows updating voice-recording
 * consent. Rendered inside the Settings page Privacy tab.
 *
 * @module PrivacySettingsTab
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useConsentStatus, CONSENT_QUERY_KEY } from "@/hooks/useConsentStatus";
import { patchConsent } from "@/lib/consentApi";
import { ROUTES } from "@/lib/routeConstants";

export function PrivacySettingsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { consentRecord, isLoading, isError, refetch } = useConsentStatus();

  const [voiceToggle, setVoiceToggle] = useState<boolean>(
    consentRecord?.voice_recording_consent ?? false,
  );
  const [isSaving, setIsSaving] = useState(false);

  // Sync toggle when server data loads or changes, but not during user interaction
  useEffect(() => {
    if (!isSaving && consentRecord !== null && consentRecord !== undefined) {
      setVoiceToggle(consentRecord.voice_recording_consent ?? false);
    }
  }, [consentRecord]);

  const handleSave = async () => {
    setIsSaving(true);
    const previous = consentRecord?.voice_recording_consent ?? false;
    try {
      const result = await patchConsent({ voice_recording_consent: voiceToggle });
      if (result) {
        await queryClient.invalidateQueries({ queryKey: CONSENT_QUERY_KEY });
        toast.success(
          t("consent.privacySettings.saveSuccess", { defaultValue: "Privacy preferences saved." }),
        );
      } else {
        setVoiceToggle(previous);
        toast.error(
          t("consent.privacySettings.saveError", {
            defaultValue: "Failed to save preferences. Please try again.",
          }),
        );
      }
    } catch {
      setVoiceToggle(previous);
      toast.error(
        t("consent.privacySettings.saveError", {
          defaultValue: "Failed to save preferences. Please try again.",
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">
            {t("consent.gate.loadingTitle", { defaultValue: "Checking your privacy settings…" })}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="pt-6 space-y-4">
          <p className="text-muted-foreground">
            {t("consent.privacySettings.loadError", {
              defaultValue: "Failed to load your privacy preferences.",
            })}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            {t("consent.privacySettings.retryButton", { defaultValue: "Retry" })}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!consentRecord) {
    return (
      <Card>
        <CardContent className="pt-6 space-y-4">
          <p className="text-muted-foreground">
            {t("consent.privacySettings.noRecord", { defaultValue: "No consent record found." })}
          </p>
          <Button asChild variant="outline">
            <Link to={ROUTES.CONSENT}>
              {t("consent.privacySettings.goToConsent", {
                defaultValue: "Review & accept privacy settings",
              })}
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const consentedDate = consentRecord.consented_at
    ? format(new Date(consentRecord.consented_at), "PPP")
    : "—";

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t("consent.privacySettings.title", { defaultValue: "Privacy Preferences" })}
        </CardTitle>
        <CardDescription>
          {t("consent.privacySettings.subtitle", {
            defaultValue: "Manage your data and recording preferences.",
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Data processing disclosure */}
        <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              {t("consent.privacySettings.dataProcessingStatus", {
                defaultValue: "Data processing consent",
              })}
            </p>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${consentRecord.data_processing_consent ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}>
              {consentRecord.data_processing_consent
                ? t("consent.privacySettings.enabled", { defaultValue: "Enabled" })
                : t("consent.privacySettings.disabled", { defaultValue: "Disabled" })}
            </span>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("consent.page.dataProcessingDisclosure", {
              defaultValue:
                "By using DeutschGenie, you agree that simulation results, practice results, and session data may be processed by external AI APIs (such as OpenAI) for analysis and personalised feedback.",
            })}
          </p>
          <div className="flex gap-4 text-xs text-muted-foreground pt-1">
            <span>
              {t("consent.privacySettings.consentedAt", { defaultValue: "Consent given on" })}:{" "}
              <span className="font-medium text-foreground">{consentedDate}</span>
            </span>
            <span>
              {t("consent.privacySettings.policyVersion", { defaultValue: "Policy version" })}:{" "}
              <span className="font-medium text-foreground">{consentRecord.consent_policy_version}</span>
            </span>
          </div>
        </div>

        <Separator />

        {/* Voice recording toggle */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="voice-toggle" className="font-medium cursor-pointer">
              {t("consent.privacySettings.voiceRecordingToggle", {
                defaultValue: "Allow voice recording",
              })}
            </Label>
            <Switch
              id="voice-toggle"
              checked={voiceToggle}
              onCheckedChange={setVoiceToggle}
              disabled={isSaving}
            />
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("consent.page.voiceRecordingDisclosure", {
              defaultValue:
                "Your voice may be recorded during simulation sessions. Recordings are used for simulation and practice-result analysis. Recordings are retained for 7 days and are automatically deleted after 7 days.",
            })}
          </p>
        </div>

        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving
            ? t("consent.privacySettings.saving", { defaultValue: "Saving…" })
            : t("consent.privacySettings.saveButton", { defaultValue: "Save preferences" })}
        </Button>
      </CardContent>
    </Card>
  );
}
